import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Spinner, Table, Badge, Card } from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const ParticipantDashboard = () => {
    const { sid, title } = useParams();

    const [loading, setLoading] = useState(true);
    const [participants, setParticipants] = useState([]);
    const [summary, setSummary] = useState(null);
    const [lastnameFilter, setLastnameFilter] = useState("");
    const [lastnames, setLastnames] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [selectedLastname, setSelectedLastname] = useState("");
    const [appliedLastname, setAppliedLastname] = useState("");
    const [appliedRole, setAppliedRole] = useState("");
    const [exporting, setExporting] = useState(false);

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);

    // =========================
    // FETCH SUMMARY
    // =========================

    useEffect(() => {
        const fetchLastnames = async () => {
            try {
                const res = await axios.get(
                    `https://apidls.onegml.com/qubisa/km/participant/participants/${sid}/lastnames`
                );

                setLastnames(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };

        fetchLastnames();
    }, [sid]);
    
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await axios.get(
                    `https://apidls.onegml.com/qubisa/km/participant/participants/${sid}/roles`
                );

                setRoles(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };

        fetchRoles();
    }, [sid]);

    useEffect(() => {
            const fetchSummary = async () => {
                try {
                    const res = await axios.get(
                        `https://apidls.onegml.com/qubisa/km/participant/survey-summary/${sid}`,
                        {
                            params: {
                                lastname: appliedLastname || undefined,
                                role: appliedRole || undefined,
                            },
                        }
                    );

                    setSummary(res.data?.data || null);
                } catch (err) {
                    console.error(err);
                }
            };

            fetchSummary();
        }, [sid, appliedLastname, appliedRole]);

    // =========================
    // FETCH PARTICIPANTS
    // =========================
    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                setLoading(true);

                const response = await axios.get(
                    `https://apidls.onegml.com/qubisa/km/participant/participants/${sid}`,
                    {
                        params: {
                            page,
                            limit,
                            lastname: appliedLastname || undefined,
                            role: appliedRole || undefined,
                        },
                    }
                );

                setParticipants(response.data?.data || []);
                setTotal(response.data?.total || 0);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchParticipants();
    }, [sid, page, appliedLastname, appliedRole]);

    useEffect(() => {
        setPage(1);
    }, [lastnameFilter]);

    const handleExport = async () => {
        try {
            setExporting(true);

            const res = await axios.get(
                `https://apidls.onegml.com/qubisa/km/participant/participants/${sid}/export`,
                {
                    params: {
                        lastname: appliedLastname || undefined,
                        role: appliedRole || undefined,
                    },
                    responseType: "blob",
                }
            );

            // Buat blob URL dari response data
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `participants_${sid}.xlsx`);

            document.body.appendChild(link);
            link.click();
            
            // Cleanup DOM element & Blob URL
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export error:", err);

            // Jika error dari response backend bertipe blob, parse balik ke text JSON agar pesan error bisa dibaca
            if (err.response && err.response.data instanceof Blob) {
                try {
                    const errorText = await err.response.data.text();
                    const errorJson = JSON.parse(errorText);
                    console.error("Backend Error Message:", errorJson);
                    // Opsional: tampilkan toast / alert pemberitahuan error
                    // alert(errorJson.detail || "Gagal mengunduh file export");
                } catch (parseErr) {
                    console.error("Gagal membaca pesan error blob", parseErr);
                }
            }
        } finally {
            setExporting(false);
        }
    };

    // =========================
    // SAFE VALUE
    // =========================
    const tokenCount = summary?.token?.count || 0;
    const tokenCompleted = summary?.token?.completed || 0;

    const notCompleted = Math.max(tokenCount - tokenCompleted, 0);

    const completedPercent =
        tokenCount > 0 ? (tokenCompleted / tokenCount) * 100 : 0;

    const notCompletedPercent =
        tokenCount > 0 ? (notCompleted / tokenCount) * 100 : 0;

    const totalPage = Math.ceil(total / limit);

    // IMPORTANT: jangan override "total"
    const pieTotal = tokenCompleted + notCompleted;

    const pieData = [
        {
            name: "Completed",
            value: tokenCompleted,
            percent: pieTotal ? (tokenCompleted / pieTotal) * 1 : 0,
        },
        {
            name: "Not Completed",
            value: notCompleted,
            percent: pieTotal ? (notCompleted / pieTotal) * 1 : 0,
        },
    ];

    const COLORS = ["#28a745", "#dc3545"];

    // =========================
    // CUSTOM LABEL %
    // =========================
    const renderCustomizedLabel = ({
        cx,
        cy,
        midAngle,
        innerRadius,
        outerRadius,
        percent,
    }) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.6;

        const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
        const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

        return (
            <text
                x={x}
                y={y}
                fill="#fff"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight="bold"
            >
                {(percent * 100).toFixed(1)}%
            </text>
        );
    };

    // =========================
    // UI
    // =========================
    return (
        <div className="p-4">
            {/* HEADER */}
            <Card className="mb-3">
                <Card.Body>
                    <h4>{decodeURIComponent(title)}</h4>
                    <small>Survey ID: {sid}</small>
                </Card.Body>
            </Card>

            {/* TOP DASHBOARD */}
            <div className="row mb-4">

                {/* PIE CHART */}
                <div className="col-md-3 d-flex justify-content-center align-items-center">
                    {summary && (
                        <PieChart width={260} height={260}>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                outerRadius={90}
                                innerRadius={50}
                                dataKey="value"
                                label={renderCustomizedLabel}
                                labelLine={false}
                                stroke="#fff"
                                strokeWidth={2}
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={index} fill={COLORS[index]} />
                                ))}
                            </Pie>

                            <Tooltip
                                formatter={(value, name) => [`${value} users`, name]}
                                contentStyle={{
                                    borderRadius: "10px",
                                    border: "none",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                }}
                            />

                            <Legend />
                        </PieChart>
                    )}
                </div>

                {/* NOT COMPLETED */}
                <div className="col-md-3">
                    <Card className="text-center p-3">
                        <h6>Total Participant</h6>
                        <h2 className="text-primary">{tokenCount}</h2>
                        <small style={{opacity: "0"}}>{notCompletedPercent.toFixed(1)}%</small>
                    </Card>
                </div>

                {/* COMPLETED */}
                <div className="col-md-3">
                    <Card className="text-center p-3">
                        <h6>Completed</h6>
                        <h2 className="text-success">{tokenCompleted}</h2>
                        <small>{completedPercent.toFixed(1)}%</small>
                    </Card>
                </div>
                <div className="col-md-3">
                    <Card className="text-center p-3">
                        <h6>Not Completed</h6>
                        <h2 className="text-danger">{notCompleted}</h2>
                        <small>{notCompletedPercent.toFixed(1)}%</small>
                    </Card>
                </div>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="text-center p-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <div>
                    <div className="mb-3 d-flex gap-2 align-items-center">
                        <div>
                            <label className="form-label">Filter Departement</label>

                            <select
                                className="form-select"
                                value={selectedLastname}
                                onChange={(e) => setSelectedLastname(e.target.value)}
                            >
                                <option value="">All</option>
                                {lastnames.map((name, idx) => (
                                    <option key={idx} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Filter Role</label>

                            <select
                                className="form-select"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                            >
                                <option value="">All</option>
                                {roles.map((name, idx) => (
                                    <option key={idx} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            className="btn btn-primary mt-4 ms-2"
                            onClick={() => {
                                setAppliedLastname(selectedLastname);
                                setAppliedRole(selectedRole)
                                setPage(1);
                            }}
                        >
                            Apply
                        </button>
                        <button
                            className="btn btn-secondary mt-4 ms-2"
                            onClick={() => {
                                setSelectedLastname("");
                                setAppliedLastname("");
                                setSelectedRole("")
                                setAppliedRole("")
                                setPage(1);
                            }}
                        >
                            Reset
                        </button>
                        <button
                            className="btn btn-success mt-4 ms-2"
                            onClick={handleExport}
                            disabled={exporting}
                        >
                            {exporting ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Exporting...
                                </>
                            ) : (
                                "Export Excel"
                            )}
                        </button>
                    </div>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>Name</th>
                                <th>Departement</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Person Evaluated</th>
                                <th>Status</th>
                                <th>Complete Date</th>
                            </tr>
                        </thead>

                        <tbody>
                            {participants.length > 0 ? (
                                participants.map((p, index) => (
                                    <tr key={p.tid}>
                                        <td>{(page - 1) * limit + index + 1}</td>
                                        <td>{p.participant_info?.firstname}</td>
                                        <td>{p.participant_info?.lastname}</td>
                                        <td>{p.participant_info?.email}</td>
                                        <td>{p.attribute_5}</td>
                                        <td>{p.attribute_6}</td>
                                        <td>
                                            <Badge bg={p.completed === "N" ? "secondary" : "success"}>
                                                {p.completed === "N" ? "Not Done" : "Done"}
                                            </Badge>
                                        </td>
                                        <td>
                                            {p.completed === "N"
                                                ? "-"
                                                : new Date(
                                                    new Date(p.completed).getTime() - 60 * 60 * 1000
                                                ).toLocaleString("id-ID")}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No Participants Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* PAGINATION */}
                    <div className="d-flex justify-content-between mt-3">
                        <button
                            className="btn btn-secondary"
                            disabled={page === 1}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            Prev
                        </button>

                        <div className="d-flex align-items-center">
                            Page {page}
                        </div>

                        <button
                            className="btn btn-primary"
                            disabled={participants.length < limit}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParticipantDashboard;